import debounce from 'lodash/debounce';
import useHMap from './hooks/useHMap'
import ApiCalls from '../../common/ApiCalls'
import { makeStyles } from '@material-ui/core'
import { markersUtil } from './utils/markersUtil'
import { useHMapContext } from './hooks/useHMapContext'
import React, { useRef, useState, useEffect } from 'react'
import { useHMapBoundInfo } from './hooks/useHMapBoundInfo'
import { mapDebounceWait } from '../../consts/settings';
import { heatmapCustomEvents } from './const/HMapEvents';
import { createHeatmapViewportWkt } from './utils/viewportBounds'
import HeatmapDateSlider from './components/HeatmapDateSlider';

let shouldReRender = true
let markersList = []
let polygonList= []

const {
  getMarkerConfig,
  markerIcon,
  statusMarkerIcon,
  getInfoWindowDiv
} = markersUtil()

const clearMarkers = (map) => {
  if (map) window.google.maps.event.clearListeners(map, 'click')
  markersList.forEach((marker) => {
    marker.setMap(null)
  })
  markersList = [...[]]
}

const transformSearchText  = (text) => {
  if (!text) return text

  /**
   * the text could contain AND or OR or space
   * if there is a space not being followed by AND or OR it should be replaced by AND
   */
  const trimmedText = text.toLowerCase().trim()
  const textArray = trimmedText.split('or')
  const res = textArray
    .map((textItem) =>
      textItem
        .split(' ')
        .filter((item) => item !== '' && item !== 'and')
        .join(' and ')
    )
    .join(' or ')

  return res
}

const getParams = (
  zoom,
  bounds,
  searchSettings,
  geoZoneState,
  heatmapGeoZone
) => {

  const isRenderingGeoZone = geoZoneState && heatmapGeoZone && heatmapGeoZone.length

  const { ne, sw } = bounds
  const { text, option } = searchSettings
  const optionToType = {
    'places/account': 'asset',
    'people': 'party',
    'service': 'service'
  }
  const queryType =
    optionToType[option?.toLowerCase()] || optionToType['places/account']

  const rectangle = createHeatmapViewportWkt(ne, sw)

  const params = {
    zoom,
    bound: rectangle,
    ...(text && { keywords: transformSearchText(text) }),
    ...(text && queryType && { types: [queryType] }),
    ...(isRenderingGeoZone && { datazones: [ ...heatmapGeoZone ] })
  }


  return { ...params }
}

// zoom to location boundries
/*function zoomToLocationBoundries(map, markersList) {
  shouldReRender = false
  const thereIsAnyMarker = markersList && markersList?.length;

  if (thereIsAnyMarker){
    const bound = new window.google.maps.LatLngBounds()

    markersList.forEach((marker) => bound.extend(marker.position))

    setTimeout(() => {
      // map.setCenter(bound.getCenter())
      // map.fitBounds(bound)
      map.panTo(bound.getCenter())
    }, 500)
  }
}*/

const processClusterData = (map, data, currentMapDetails, fields) => {
  // console.log(" =>", data)
  // console.log("currentMapDetails =>", currentMapDetails)
  const pins = data?.pins
  const infoWindow = new window.google.maps.InfoWindow()

  if (!!pins) {
    pins.forEach((pin, idx) => {
      const latitude = Number(pin.lat)
      const longitude = Number(pin.long)
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        const hasMetrics = Array.isArray(pin.metrics) && pin.metrics.length > 0;
        const metricsForUI = (hasMetrics ? pin.metrics : [
          {
            ref: 'number',
            label: 'Count',
            total: Number(pin.count || 0),
            percent: 0,
            field_id: null
          }
        ])
        // Config by size
        const { size, color, shadow, label } = getMarkerConfig(
          [...metricsForUI],
          currentMapDetails, fields
        )
        const status = pin?.data_status ?? pin?.dataStatus
        const isAttentionMarker = Number(pin?.count || 1) === 1
          && (status === 'empty' || status === 'partial')

        const marker = new window.google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map,
          ...(!isAttentionMarker && label && {
            label: {
              text: label.text,
              color: 'white',
              fontSize: '10px',
              fontWeight: 'bold'
            }
          }),
          optimized: false,
          zIndex: status === 'empty' ? 1 : 2,
          title: pin?.location_name || pin?.field || 'Heatmap location',
          icon: isAttentionMarker
            ? statusMarkerIcon({
                status,
                zoom: map.getZoom(),
                missingFieldCount: Number(
                  pin?.missing_field_count ?? pin?.missingFieldCount ?? 0
                )
              })
            : markerIcon({ size, color, shadow, label })
        })

        const openInfoWindow = () => {
          infoWindow.close()
          infoWindow.setContent(
            getInfoWindowDiv(idx, { ...pin, metrics: metricsForUI }, currentMapDetails, fields)
          )
          infoWindow.open(marker.getMap(), marker)
        }

        marker.addListener('mouseover', openInfoWindow)
        marker.addListener('mouseout', () => {
          if (!marker.heatmapInfoWindowPinned) infoWindow.close()
        })
        marker.addListener('click', () => {
          marker.heatmapInfoWindowPinned = true
          openInfoWindow()
          shouldReRender = false
          map.panTo(marker.position)
        })
        infoWindow.addListener('closeclick', () => {
          marker.heatmapInfoWindowPinned = false
        })

        markersList.push(marker)
      }
    })
  }

  if (map) {
    window.google.maps.event.addListener(map, 'click', () => {
      markersList.forEach(marker => {
        marker.heatmapInfoWindowPinned = false
      })
      infoWindow.close()
    })

    // Draw on Map
    // polygonList.forEach((item) => item.reDraw && item.polygon.setMap(map))
    // polygonList.forEach((item) => item.setMap(map))

    // Zoom to location
    // zoomToLocationBoundries(map, markersList)
  }
}

// Polygons
const clearPolygons = (map) => map && polygonList.forEach((item) => item.setMap(null))

const wktToCoordinate = (wkt) => {
  const wktCoordinatesArray = wkt
    .replace('POLYGON', '')
    .replace('((', '')
    .replace('))', '')
    .split(',')

    const polygonCooridinates = []
    wktCoordinatesArray.forEach((point) => {
      const [lng, lat] = point.split(' ')
      polygonCooridinates.push({
        lat: Number(lat),
        lng: Number(lng)
      })
    })

    return [...polygonCooridinates]
}

const processPolygonsData = (allGeoZones, heatmapGeoZone, map) => {
  // Extract all WKTs
  const allWKTs = []
  allGeoZones
    .filter((item) => heatmapGeoZone.includes(item.id))
    .forEach((item) => {
      const geoZoneValid = item.geo_zones && item.geo_zones.length
      geoZoneValid &&
        item.geo_zones.forEach((item) => item.wkt && !!allWKTs.push(item.wkt))
    })

  // Transform WKT to Coordinates
  polygonList = []
  allWKTs.forEach(
    (wkt) => {
      if (wkt.includes('POLYGON')) {
        const coordinates  = wktToCoordinate(wkt)

         polygonList.push(
           new window.google.maps.Polygon({
             paths: coordinates,
             strokeColor: '#FF0000',
             strokeOpacity: 0.6,
             strokeWeight: 1,
             fillColor: '#FF0000',
             fillOpacity: 0.2
           })
         )

         polygonList.forEach((item) => item.setMap(map))
      }
  })
}

const HMap = ({ leftPanelOpen = true }) => {
  const classes = useStyles({ leftPanelOpen })
  const latestRequest = useRef(null);
  const ref = useRef(null)
  const [map, setMap] = useState()
  const {
    history,
    requestStatusInfo,
    currentMapInfo,
    dynamicFieldsRef,
    allHeatMapsList,
    searchConfig,
    snackbar,
    allGeoZoneList,
    heatmapGeoZoneList,
    geoZoneStateInfo
  } = useHMapContext()
  const [, setRequestStatus] = requestStatusInfo;
  const apiAbortControllerRef = useRef(null);
  const handleMapParamsChangeDebouncedRef = useRef(null);


  const [currentMap, ,] = currentMapInfo
  const [allHeatMaps, setAllHeatMaps] = allHeatMapsList
  const [searchSettings] = searchConfig
  const [isSnackbarOpen, setIsSnackbarOpen] = snackbar
  const [allGeoZones,] = allGeoZoneList
  const [geoZoneState,] = geoZoneStateInfo
  const [heatmapGeoZone,] = heatmapGeoZoneList

  useHMap(ref, map, setMap)
  const { zoomBounds } = useHMapBoundInfo(map)

  useEffect(() => {
    // Clear polygons on the map when the component unloads
    return () => {
      polygonList = []
      apiAbortControllerRef.current?.abort();
      clearMarkers(map);
    }

    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // Draw polygons on the map if any
    if (allGeoZones !== 'loading' && heatmapGeoZone && map) {
      clearPolygons(map)
      processPolygonsData(allGeoZones, heatmapGeoZone, map)
    }

    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [heatmapGeoZone])

  const handleMapParamsChange = (currentMapId = undefined) => {
    if (!shouldReRender) {
      shouldReRender = true;
      return;
    }

    const mapId = currentMapId || currentMap;

    const { zoom, bounds } = zoomBounds;

    if (!mapId || !zoom || !bounds) {
      return;
    }

    if (apiAbortControllerRef.current) {
      apiAbortControllerRef.current.abort();
    }

    apiAbortControllerRef.current = new AbortController();
    const signal = apiAbortControllerRef.current.signal;
    clearMarkers(map);
    setRequestStatus('loading');

    // load the heatmap data on the region
    const params = getParams(
      zoom,
      bounds,
      searchSettings,
      geoZoneState,
      heatmapGeoZone
    );

    Object.assign(params, history.params);

    //Load heatmap data
    const currentMapArray = allHeatMaps.filter(
      (item) => item.id === mapId
    );
    const currentMapDetails = currentMapArray?.length && {
      ...currentMapArray[0]
    };

    const requestHeatmaps = async () => {
      try {
        const heatmapData = await ApiCalls().queryAHeatmap(mapId, params, signal);

        if (signal.aborted) {
          return;
        }

        const clusters = heatmapData?.data?.data?.clusters;

        clearMarkers(map);
        // clearPolygons(map)
        /** Default map vs all the other maps ?????????*/
        // const clusterData = clusters && clusters.length && clusters[0] // -----------???

        if (!currentMapDetails?.metrics) {
          try {
            const aHeatmap = await ApiCalls().getAHeatmap(mapId, signal);
            if (signal.aborted) {
              return;
            }

            const metrics = aHeatmap?.data?.data?.metrics;

            if (metrics) {
              setAllHeatMaps([
                ...allHeatMaps.map((item) =>
                  item.id === mapId
                    ? { ...item, metrics: [...metrics] }
                    : { ...item }
                )
              ]);

              currentMapDetails.metrics = [...metrics];
            }
          } catch (err) {
            if (signal.aborted) return;
            setIsSnackbarOpen({
              ...isSnackbarOpen,
              message: 'The heatmap details could not be retrieved',
              severity: 'fail',
              isOpen: true
            });

          }
        }

        if (currentMapDetails.metrics) {
          clusters?.forEach(cluster => {
            processClusterData(map, cluster, currentMapDetails, dynamicFieldsRef.current);
          });
        }
        if (!signal.aborted) setRequestStatus('');
      } catch (err) {
        if (signal.aborted) return;
        setRequestStatus('error');
        setIsSnackbarOpen({
          ...isSnackbarOpen,
          message: 'The clusters could not be retrieved',
          severity: 'fail',
          isOpen: true
        });
      }
    };

    requestHeatmaps();

    return () => apiAbortControllerRef.current?.abort();
  }

  latestRequest.current = handleMapParamsChange;

  useEffect(() => {
    handleMapParamsChangeDebouncedRef.current = debounce((id) => latestRequest.current?.(id), mapDebounceWait);

    return () => {
      handleMapParamsChangeDebouncedRef.current?.cancel(); // Cancel debounced calls
    };
  }, []);

  useEffect(() => {
    // Listener from event inside the HMapSearch.js component
    const listener = debounce((event) => {
      const currentMapId = event.detail.currentMap;
      latestRequest.current?.(currentMapId);
    }, mapDebounceWait);
    document.addEventListener(heatmapCustomEvents.START_SEARCH, listener);

    return () => {
      listener.cancel();
      document.removeEventListener(heatmapCustomEvents.START_SEARCH, listener);
    };
    // XXX Fix this in the future
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleMapParamsChangeDebouncedRef.current?.cancel();
    handleMapParamsChange();
    return () => apiAbortControllerRef.current?.abort();
    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [currentMap, history.date, history.timezone, history.revision]);

  useEffect(() => {
    if (handleMapParamsChangeDebouncedRef.current) {
      handleMapParamsChangeDebouncedRef.current();
    }

    return () => apiAbortControllerRef.current?.abort(); // Abort any requests
    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [zoomBounds, heatmapGeoZone, searchSettings.text, searchSettings.option, geoZoneState]);

  return (
    <>
      <div className={classes.root} ref={ref} />
      <div className={classes.historySlider}>
        <HeatmapDateSlider
          date={history.date}
          onChange={history.setDate}
          today={history.today}
        />
      </div>
    </>
  )
}

export default React.memo(HMap)

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '101%'
  },
  historySlider: {
    bottom: 24,
    left: (props) => props.leftPanelOpen
      ? 'calc(475px + ((100% - 475px) / 2))'
      : '50%',
    position: 'absolute',
    transform: 'translateX(-50%)',
    transition: 'left .15s, width .15s',
    width: (props) => props.leftPanelOpen
      ? 'clamp(260px, calc(100% - 535px), 560px)'
      : 'min(560px, calc(100% - 48px))',
    zIndex: 9
  }
}))
